import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Plus, Pencil, Trash2, Loader2, GripVertical, Utensils, Package, Truck, Sparkles, Flower2, Wine } from 'lucide-react';
import { categoriesAPI } from '../../services/api';
import { useToast } from '../../hooks/use-toast';

const CATEGORY_TYPES = [
  { id: 'menu', label: 'Menu Categories', icon: Utensils, color: 'bg-blue-500' },
  { id: 'bar', label: 'Bar Categories', icon: Wine, color: 'bg-amber-500' },
  { id: 'hors_doeuvres', label: 'Hors d\'Oeuvres', icon: Utensils, color: 'bg-rose-500' },
  { id: 'late_night', label: 'Late Night', icon: Utensils, color: 'bg-indigo-500' },
  { id: 'buffet', label: 'Buffet', icon: Utensils, color: 'bg-teal-500' },
  { id: 'plated', label: 'Plated Dinner', icon: Utensils, color: 'bg-cyan-500' },
  { id: 'addon', label: 'Add-on Categories', icon: Sparkles, color: 'bg-purple-500' },
  { id: 'rental', label: 'Rental Categories', icon: Truck, color: 'bg-green-500' },
  { id: 'equipment', label: 'Equipment Categories', icon: Package, color: 'bg-orange-500' },
  { id: 'decor', label: 'Decor Categories', icon: Flower2, color: 'bg-pink-500' },
];

const CategoryManagement = () => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState({});
  const [activeType, setActiveType] = useState('menu');
  const [showDialog, setShowDialog] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    sort_order: 0
  });

  useEffect(() => {
    loadCategories();
  }, []);

  const loadCategories = async () => {
    try {
      setLoading(true);
      const data = await categoriesAPI.getAll();
      setCategories(data);
    } catch (err) {
      // If no categories exist, seed defaults
      if (err.response?.status === 404 || Object.keys(categories).length === 0) {
        try {
          await categoriesAPI.seedDefaults();
          const data = await categoriesAPI.getAll();
          setCategories(data);
        } catch (seedErr) {
          console.error('Error seeding categories:', seedErr);
        }
      }
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (category = null) => {
    if (category) {
      setEditingCategory(category);
      setFormData({
        name: category.name,
        description: category.description || '',
        sort_order: category.sort_order || 0
      });
    } else {
      setEditingCategory(null);
      const currentCategories = categories[activeType] || [];
      setFormData({
        name: '',
        description: '',
        sort_order: currentCategories.length + 1
      });
    }
    setShowDialog(true);
  };

  const handleSave = async () => {
    if (!formData.name.trim()) {
      toast({ title: 'Error', description: 'Category name is required', variant: 'destructive' });
      return;
    }

    setSaving(true);
    try {
      if (editingCategory) {
        await categoriesAPI.update(editingCategory.id, formData);
        toast({ title: 'Success', description: 'Category updated' });
      } else {
        await categoriesAPI.create({ ...formData, type: activeType });
        toast({ title: 'Success', description: 'Category created' });
      }
      setShowDialog(false);
      loadCategories();
    } catch (err) {
      toast({ 
        title: 'Error', 
        description: err.response?.data?.detail || 'Failed to save category', 
        variant: 'destructive' 
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (category) => {
    if (category.is_system) {
      toast({ title: 'Error', description: 'Cannot delete system category', variant: 'destructive' });
      return;
    }
    
    if (!window.confirm(`Delete category "${category.name}"? This may affect existing items.`)) {
      return;
    }

    try {
      await categoriesAPI.delete(category.id);
      toast({ title: 'Success', description: 'Category deleted' });
      loadCategories();
    } catch (err) {
      toast({ 
        title: 'Error', 
        description: err.response?.data?.detail || 'Failed to delete category', 
        variant: 'destructive' 
      });
    }
  };

  const handleSeedDefaults = async () => {
    try {
      const result = await categoriesAPI.seedDefaults();
      toast({ title: 'Success', description: result.message });
      loadCategories();
    } catch (err) {
      toast({ title: 'Error', description: 'Failed to seed defaults', variant: 'destructive' });
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="h-8 w-8 animate-spin text-[#1e3a5f]" />
      </div>
    );
  }

  const currentCategories = categories[activeType] || [];

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-xl font-bold text-[#1e3a5f]">Category Management</h2>
          <p className="text-sm text-gray-500">Manage categories for menus, rentals, equipment, and more</p>
        </div>
        <Button variant="outline" onClick={handleSeedDefaults} className="text-sm">
          Reset to Defaults
        </Button>
      </div>

      <Tabs value={activeType} onValueChange={setActiveType}>
        <TabsList className="grid grid-cols-5 w-full">
          {CATEGORY_TYPES.map(type => (
            <TabsTrigger key={type.id} value={type.id} className="flex items-center gap-2">
              <type.icon className="h-4 w-4" />
              <span className="hidden md:inline">{type.label.replace(' Categories', '')}</span>
            </TabsTrigger>
          ))}
        </TabsList>

        {CATEGORY_TYPES.map(type => (
          <TabsContent key={type.id} value={type.id} className="mt-4">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between py-4">
                <CardTitle className="text-lg flex items-center gap-2">
                  <type.icon className={`h-5 w-5 p-1 rounded text-white ${type.color}`} />
                  {type.label}
                </CardTitle>
                <Button onClick={() => handleOpenDialog()} className="bg-[#1e3a5f] hover:bg-[#2d4a6f]" data-testid={`add-${type.id}-category-btn`}>
                  <Plus className="h-4 w-4 mr-2" /> Add Category
                </Button>
              </CardHeader>
              <CardContent>
                {currentCategories.length === 0 ? (
                  <p className="text-gray-500 text-center py-8">No categories yet. Click "Add Category" to create one.</p>
                ) : (
                  <div className="space-y-2">
                    {currentCategories.sort((a, b) => a.sort_order - b.sort_order).map((category, index) => (
                      <div
                        key={category.id}
                        className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                      >
                        <div className="flex items-center gap-3">
                          <GripVertical className="h-4 w-4 text-gray-400" />
                          <div>
                            <p className="font-medium">{category.name}</p>
                            {category.description && (
                              <p className="text-xs text-gray-500">{category.description}</p>
                            )}
                          </div>
                          {category.is_system && (
                            <span className="text-xs px-2 py-0.5 bg-blue-100 text-blue-700 rounded">System</span>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleOpenDialog(category)}
                            className="h-8 w-8 p-0"
                            data-testid={`edit-category-${category.id}`}
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDelete(category)}
                            className="h-8 w-8 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                            disabled={category.is_system}
                            data-testid={`delete-category-${category.id}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        ))}
      </Tabs>

      {/* Add/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingCategory ? 'Edit Category' : 'Add New Category'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="name">Category Name *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., Premium Buffets"
                data-testid="category-name-input"
              />
            </div>
            <div>
              <Label htmlFor="description">Description (Optional)</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Brief description of this category..."
                rows={2}
              />
            </div>
            <div>
              <Label htmlFor="sort_order">Sort Order</Label>
              <Input
                id="sort_order"
                type="number"
                value={formData.sort_order}
                onChange={(e) => setFormData({ ...formData, sort_order: parseInt(e.target.value) || 0 })}
                min="0"
              />
              <p className="text-xs text-gray-500 mt-1">Lower numbers appear first</p>
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setShowDialog(false)}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving} className="bg-[#1e3a5f] hover:bg-[#2d4a6f]" data-testid="save-category-btn">
              {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
              {editingCategory ? 'Update' : 'Create'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CategoryManagement;
